import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, ListChecks, Check } from 'lucide-react';
import type { ProgressCoaching } from './types';

interface ProgressCoachingResultsProps {
  coaching: ProgressCoaching;
  goals: { id: string; title: string }[];
  addedGoalTitles?: Set<string>;
  onAddFocusAreaAsGoal?: (title: string, description: string) => void;
  onAddFocusAreaAsHabit?: (goalId: string, title: string) => void;
}

export const ProgressCoachingResults = ({
  coaching,
  goals,
  addedGoalTitles,
  onAddFocusAreaAsGoal,
  onAddFocusAreaAsHabit,
}: ProgressCoachingResultsProps) => {
  const [addingHabitFor, setAddingHabitFor] = useState<number | null>(null);

  return (
    <div className="space-y-4">
      <p className="text-sm">{coaching.overall_assessment}</p>

      {coaching.highlights?.length > 0 && (
        <div className="p-3 bg-green-500/5 rounded-lg border border-green-500/20">
          <h5 className="font-medium text-sm text-green-600 mb-2">Highlights</h5>
          <ul className="text-sm space-y-1">
            {coaching.highlights.map((highlight, i) => (
              <li key={i} className="text-muted-foreground">• {highlight}</li>
            ))}
          </ul>
        </div>
      )}

      {coaching.focus_areas?.length > 0 && (
        <div className="space-y-2">
          <h5 className="font-medium text-sm">Focus Areas</h5>
          {coaching.focus_areas.map((area, i) => (
            <div key={i} className="p-3 bg-muted/30 rounded-lg border space-y-2">
              <p className="font-medium text-sm">{area.area}</p>
              <p className="text-sm text-muted-foreground">{area.suggestion}</p>
              <p className="text-sm text-primary/80 italic">{area.encouragement}</p>

              {(onAddFocusAreaAsGoal || onAddFocusAreaAsHabit) && (
                <div className="pt-2 border-t border-border/50 space-y-2">
                  {addingHabitFor === i ? (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">Add as habit to which goal?</p>
                      <div className="flex flex-wrap gap-2">
                        {goals.map((goal) => (
                          <Button
                            key={goal.id}
                            size="sm"
                            variant="outline"
                            className="text-xs h-7"
                            onClick={() => {
                              onAddFocusAreaAsHabit?.(goal.id, area.area);
                              setAddingHabitFor(null);
                            }}
                          >
                            {goal.title.length > 25 ? goal.title.slice(0, 25) + '...' : goal.title}
                          </Button>
                        ))}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-xs h-7"
                          onClick={() => setAddingHabitFor(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {onAddFocusAreaAsGoal && (
                        addedGoalTitles?.has(area.area) ? (
                          <span className="text-xs text-green-600 flex items-center gap-1 h-7 px-2">
                            <Check className="w-3 h-3" /> Added as Goal
                          </span>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1 text-xs h-7"
                            onClick={() => onAddFocusAreaAsGoal(area.area, area.suggestion)}
                          >
                            <Plus className="w-3 h-3" />
                            Add as Goal
                          </Button>
                        )
                      )}
                      {onAddFocusAreaAsHabit && goals.length > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-xs h-7"
                          onClick={() => setAddingHabitFor(i)}
                        >
                          <ListChecks className="w-3 h-3" />
                          Add as Habit
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {coaching.next_steps?.length > 0 && (
        <div>
          <h5 className="font-medium text-sm mb-2">Next Steps</h5>
          <ol className="text-sm space-y-1 list-decimal list-inside">
            {coaching.next_steps.map((step, i) => (
              <li key={i} className="text-muted-foreground">{step}</li>
            ))}
          </ol>
        </div>
      )}

      {coaching.motivational_message && (
        <div className="p-3 bg-primary/5 rounded-lg border border-primary/20 text-center">
          <p className="text-sm italic">"{coaching.motivational_message}"</p>
        </div>
      )}
    </div>
  );
};
